import { Flex, Select, Space, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { AuthGuard } from '~/app/auth/guard/AuthGuard';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import {
  OPTIMIZER_PAGE_SIZE,
  WORK_ORDER_KANBAN_PATH,
} from '~/app/components/maintenance/work-orders/optimizer/constants';
import { OptimizerSuggestionsBody } from '~/app/components/maintenance/work-orders/optimizer/components/OptimizerSuggestionsBody/OptimizerSuggestionsBody';
import { useWorkOrderOptimizerPage } from './hooks/useWorkOrderOptimizerPage';

function WorkOrderOptimizerPageView() {
  const { t } = useTranslation('maintenance');
  const navigate = useNavigate();
  const page = useWorkOrderOptimizerPage();

  return (
    <Space
      direction="vertical"
      size={16}
      style={{
        width: '100%',
        background: WORK_ORDER_UI.pageBg,
        padding: '4px 0 24px',
        boxSizing: 'border-box',
      }}
    >
      <div>
        <Link
          to={WORK_ORDER_KANBAN_PATH}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 8,
            color: WORK_ORDER_UI.suggestionLink,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <ArrowLeftOutlined />
          {t('workOrders.optimizerPage.backToKanban')}
        </Link>
        <Typography.Title
          level={3}
          style={{ margin: '0 0 4px', color: WORK_ORDER_UI.sidebarNavy }}
        >
          {t('workOrders.optimizerPage.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('workOrders.optimizerPage.description')}
        </Typography.Paragraph>
      </div>

      <Flex gap={12} wrap="wrap" align="center" justify="space-between">
        <Select
          allowClear
          style={{ minWidth: 220 }}
          placeholder={t('workOrders.kanban.filters.allSites')}
          value={page.siteFilter || undefined}
          options={page.siteFilterOptions}
          onChange={(value) => {
            page.setSiteFilter(value ?? '');
            page.list.setPage(1);
          }}
        />
        {!page.list.isLoading ? (
          <Typography.Text type="secondary">
            {t('workOrders.optimizerPage.totalLabel', {
              total: page.list.total,
            })}
          </Typography.Text>
        ) : null}
      </Flex>

      <div
        style={{
          background: '#fff',
          border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
          borderRadius: 12,
          padding: 16,
        }}
      >
        <OptimizerSuggestionsBody
          clusters={page.list.clusters}
          canManage={page.list.canManage}
          isLoading={page.list.isLoading || page.isLoadingBoard}
          isError={page.list.isError}
          total={page.list.total}
          page={page.list.page}
          pageSize={OPTIMIZER_PAGE_SIZE}
          showPagination
          pendingApply={page.list.pendingApply}
          isApplying={page.list.isApplying}
          onPageChange={page.list.setPage}
          onSelectWorkOrder={() => {
            void navigate(WORK_ORDER_KANBAN_PATH);
          }}
          onOpenApply={page.list.openApply}
          onPendingTargetChange={(id) =>
            page.list.setPendingApply((prev) =>
              prev ? { ...prev, targetWorkOrderId: id } : prev,
            )
          }
          onCancelApply={() => page.list.setPendingApply(null)}
          onConfirmApply={() => {
            void page.list.confirmApply();
          }}
        />
      </div>
    </Space>
  );
}

export function WorkOrderOptimizerPageContainer() {
  return (
    <AuthGuard requiredPermissions={[PermissionKey.WORK_ORDER_READ]}>
      <WorkOrderOptimizerPageView />
    </AuthGuard>
  );
}
